import React, { useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { MdSearchOff, MdSearch } from "react-icons/md";

import styles from "./Item.module.scss";
import { selectClusterList } from "../../store/slices/clusterListSlice";
import { useItem } from "./useItem";
import { useSearch } from "../../common/components/search/useSearch";
import { Search } from "../../common/components/search/Search";
import { PageHeader } from "../../common/components/pageHeader/PageHeader";
import { EmptyState } from "../../common/components/emptyState/EmptyState";
import { ItemCard } from "../../common/components/itemCard/ItemCard";
import { ClusterTypeChip } from "../../common/components/clusterTypeChip/ClusterTypeChip";
import { ClusterTypes } from "../../common/types/enums";
import { clusterTypeToText } from "../../common/utils/functions/enumToText";

export const Item: React.FC = () => {
  const clusters = useSelector(selectClusterList);
  const [searchParams, setSearchParams] = useSearchParams();

  const { query, setQuery, clearSearch, isDebouncing } = useSearch(400, true);
  const {
    items,
    total,
    ready,
    isFetching,
    hasMore,
    loadMore,
    lastItemRef,
    isFetchingMore,
  } = useItem();

  const activeType = searchParams.get("clusterType");

  const availableTypes = useMemo(() => {
    const set = new Set<number>();
    clusters.forEach((c) => {
      if (typeof c.type === "number") set.add(c.type);
    });
    return Array.from(set).sort((a, b) => a - b);
  }, [clusters]);

  const handleTypeSelect = (type: number | null) => {
    const newParams = new URLSearchParams(searchParams);
    if (type === null) {
      newParams.delete("clusterType");
    } else {
      newParams.set("clusterType", String(type));
    }
    setSearchParams(newParams);
  };

  if (!ready) return null;

  const isBusy = isFetching || isDebouncing;

  return (
    <div className={styles.Page}>
      <Helmet>
        <title>
          {activeType
            ? `${clusterTypeToText(Number(activeType) as ClusterTypes)} — ЛОГМОЛЛ`
            : "Каталог товаров — ЛОГМОЛЛ"}
        </title>
      </Helmet>

      <PageHeader title="Каталог" subtitle={`${total} товаров`} />

      <div className={styles.SearchWrap}>
        <Search
          value={query}
          onChange={setQuery}
          clear={clearSearch}
          placeHolder="Поиск по названию товара..."
        />
      </div>

      {availableTypes.length > 0 && (
        <div className={styles.FiltersWrap}>
          <div className={styles.FiltersNav}>
            {availableTypes.map((type) => {
              const isActive = Number(activeType) === type;
              return (
                <ClusterTypeChip
                  key={type}
                  type={type}
                  isActive={isActive}
                  compact
                  onClick={() => handleTypeSelect(isActive ? null : type)}
                />
              );
            })}
          </div>
        </div>
      )}

      {items.length === 0 && !isBusy ? (
        query.trim() ? (
          <EmptyState
            icon={MdSearchOff}
            title="Ничего не найдено"
            description={`По запросу «${query}» нет товаров.`}
          />
        ) : (
          <EmptyState
            icon={MdSearch}
            title="Товаров пока нет"
            description="Попробуйте изменить фильтры или выбрать другой город."
          />
        )
      ) : items.length === 0 && isBusy ? (
        <div style={{ minHeight: 400 }} />
      ) : (
        <>
          <div className={styles.Grid}>
            {items.map((item, index) => (
              <ItemCard
                key={item.id}
                item={item}
                lastItemRef={
                  index === items.length - 1 ? lastItemRef : undefined
                }
              />
            ))}
          </div>

          {isFetchingMore && (
            <div
              style={{
                textAlign: "center",
                padding: "20px",
                color: "var(--text-secondary)",
              }}
            >
              Загрузка...
            </div>
          )}
        </>
      )}
    </div>
  );
};